import React, { useState } from "react";
import { toast } from "react-toastify";
import { postUserApi } from "../../services/userApi";
import { Box, Button, Typography, MenuItem } from "@mui/material";
import { CustomTextField } from "../ui/CustomTextField.tsx";
import { CheckCircle, XCircle, ChevronDown } from "lucide-react";

enum UserGroup {
  ADMIN = "admin",
  USER = "user",
}

enum ToastType {
  WARNING = "warn",
  SUCCESS = "success",
  ERROR = "error",
}

interface UserAttributes {
  email: string;
  email_verified: boolean;
  sub: string;
  enabled: boolean;
  status: string;
  user_status: string;
}

interface UserDataType {
  username: string;
  attributes: UserAttributes;
  groups: string[];
}

interface AdminUserProps {
  textConfig: Record<string, string>;
  userData: UserDataType;
  email: string;
  group: UserGroup | string;
  setEmail: (email: string) => void;
  setGroup: (group: string) => void;
  username: string;
  setUsername: (username: string) => void;
  isLoading: boolean;
  handleSearch: () => Promise<void>;
}
function AdminUser({
  textConfig,
  userData,
  email,
  group,
  setEmail,
  setGroup,
  username,
  setUsername,
  isLoading,
  handleSearch,
}: AdminUserProps) {
  const [loading, setLoading] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const showToast = (type: ToastType, message: string) => {
    toast.dismiss();
    toast[type](message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleSubmit = async () => {
    if (!validateEmail(email)) {
      showToast(ToastType.ERROR, "Por favor ingrese un email válido");
      return;
    }

    setLoading(true);
    showToast(ToastType.WARNING, textConfig.ADMIN_TOAST_USER_SAVING);

    const updateData = {
      username,
      attributes: { email },
      groups: {
        [UserGroup.ADMIN]: group === UserGroup.ADMIN,
        [UserGroup.USER]: group === UserGroup.USER,
      },
    };

    try {
      await postUserApi(updateData);
      showToast(ToastType.SUCCESS, textConfig.ADMIN_TOAST_USER_UPDATED);
    } catch (error) {
      showToast(ToastType.ERROR, textConfig.ADMIN_TOAST_USER_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const renderVerificationIcon = (isVerified: boolean | string) => {
    return isVerified === "true" || isVerified === true ? (
      <CheckCircle size={20} className="text-green-500" />
    ) : (
      <XCircle size={20} className="text-red-500" />
    );
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {userData && (
        <Box sx={{ mt: 2, backgroundColor: "#27272A", p: 3, borderRadius: 2 }}>
          <Typography
            variant="h6"
            color="white"
            sx={{ mb: 3, fontWeight: 600 }}
          >
            {textConfig.ADMIN_USER_TEXT_DATA_USER}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box>
              <Typography color="white" sx={{ mb: 1, fontWeight: 500 }}>
                {textConfig.ADMIN_USER_TEXT_USERNAME}
              </Typography>
              <Typography color="gray" sx={{ fontSize: "0.9rem" }}>
                {userData.username}
              </Typography>
            </Box>

            <Box>
              <Typography color="white" sx={{ mb: 1, fontWeight: 500 }}>
                {textConfig.ADMIN_USER_TEXT_EMAIL}{" "}
                <span style={{ color: "#FF3D3D" }}>*</span>
              </Typography>
              <CustomTextField
                isFullWidth
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                error={!validateEmail(email) && email !== ""}
                helperText={
                  !validateEmail(email) && email !== "" ? "Email inválido" : ""
                }
                required
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography color="white" sx={{ fontWeight: 500 }}>
                {textConfig.ADMIN_USER_TEXT_EMAIL_VERIFIED}:
              </Typography>
              {renderVerificationIcon(userData.attributes.email_verified)}
            </Box>

            <Box>
              <Typography color="white" sx={{ mb: 1, fontWeight: 500 }}>
                {textConfig.ADMIN_USER_TEXT_SUB}
              </Typography>
              <Typography color="gray" sx={{ fontSize: "0.9rem" }}>
                {userData.attributes.sub}
              </Typography>
            </Box>

            <Box>
              <Typography color="white" sx={{ mb: 1, fontWeight: 500 }}>
                {textConfig.ADMIN_USER_TEXT_GROUPS}{" "}
                <span style={{ color: "#FF3D3D" }}>*</span>
              </Typography>
              <CustomTextField
                select
                isFullWidth
                value={group}
                onChange={(e) => setGroup(e.target.value as UserGroup)}
                required
                sx={{
                  "& .MuiInputBase-root": {
                    height: "40px",
                    backgroundColor: "#2D2D2D",
                  },
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    height: "40px !important",
                    padding: "0 14px",
                    color: "#fff",
                  },
                  "& .MuiSelect-icon": {
                    color: "#fff",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#22c55e",
                    borderWidth: "1px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#22c55e",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#22c55e",
                    borderWidth: "2px",
                  },
                }}
                SelectProps={{
                  IconComponent: (props) => (
                    <ChevronDown
                      {...props}
                      size={20}
                      style={{
                        color: "#fff",
                        right: 8,
                      }}
                    />
                  ),
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        backgroundColor: "#3A3A3A",
                        borderRadius: "8px",
                        marginTop: "4px",
                        "& .MuiList-root": {
                          padding: "4px",
                        },
                        "& .MuiMenuItem-root": {
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: "#4A4A4A",
                          },
                          "&.Mui-selected": {
                            backgroundColor: "#22C55E",
                            "&:hover": {
                              backgroundColor: "#1EA34A",
                            },
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value={UserGroup.ADMIN}>
                  {textConfig.ADMIN_USER_TEXT_ADMINISTRATOR}
                </MenuItem>
                <MenuItem value={UserGroup.USER}>
                  {textConfig.ADMIN_USER_TEXT_USER}
                </MenuItem>
              </CustomTextField>
            </Box>

            {/* Account Status Fields */}
            <Box>
              <Typography color="white" sx={{ mb: 1, fontWeight: 500 }}>
                Estado de la cuenta
              </Typography>
              <Typography color="gray" sx={{ fontSize: "0.9rem" }}>
                {userData.attributes.status}
              </Typography>
            </Box>

            <Box>
              <Typography color="white" sx={{ mb: 1, fontWeight: 500 }}>
                Estado del usuario
              </Typography>
              <Typography color="gray" sx={{ fontSize: "0.9rem" }}>
                {userData.attributes.user_status}
              </Typography>
            </Box>

            <Box>
              <Typography color="white" sx={{ mb: 1, fontWeight: 500 }}>
                Cuenta habilitada
              </Typography>
              {renderVerificationIcon(userData.attributes.enabled)}
            </Box>
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                backgroundColor: "#22C55E",
                color: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#16A34A",
                },
                "&:disabled": {
                  backgroundColor: "#1F1F1F",
                  color: "#666666",
                },
                textTransform: "none",
                minWidth: "100px",
                borderRadius: "8px",
                padding: "8px 16px",
              }}
            >
              {loading
                ? textConfig.ADMIN_USER_BUTTON_SAVING
                : textConfig.ADMIN_USER_BUTTON_SAVE}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default AdminUser;
